import { useState, FormEvent, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store';
import { setTokens, updateUser } from '@/store/slices/authSlice';
import { useValidate2FAMutation } from '@/store/api/apiSlice';
import AuthSingleCardLayout from '@/components/auth/AuthSingleCardLayout';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { FiMail, FiLock, FiAlertCircle, FiShield } from 'react-icons/fi';
import { SiGoogle } from 'react-icons/si';
import { toast } from 'react-toastify';
import { storage } from '@/utils/storage';
import { getTenantSlug } from '@/utils/tenant';
import { getApiBaseUrl } from '@/utils/apiBase';
import { getBrowserTimeZone } from '@/utils/dateTime';
import { resolveAuthenticatedHomeAfterAuth } from '@/utils/productLicense';
import { persistEntryProductFromUrl, readEntryProductId } from '@/utils/entryProduct';
import { navigateAfterLogin, isWorkspaceRouterEnabled } from '@/utils/postLoginNavigation';
import { PRODUCT_BY_ID } from '@/config/products';
import { useDiscoverTenantForLoginMutation } from '@/store/api/apiSlice';
import TenantLogoImage from '@/components/common/TenantLogoImage';
import type { User } from '@/types';

const LOGIN_SUCCESS_NAV_DELAY_MS = 520;

/**
 * Login Page Component
 * Handles user authentication
 */
export default function Login() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const entryProductId = useMemo(
    () => readEntryProductId(searchParams.toString()),
    [searchParams],
  );

  useEffect(() => {
    persistEntryProductFromUrl(searchParams.toString());
  }, [searchParams]);
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const apiUrl = getApiBaseUrl();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [signInSuccess, setSignInSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSSOProviders, setAvailableSSOProviders] = useState<string[]>([]);
  const [loadingSSOProviders, setLoadingSSOProviders] = useState(true);

  // 2FA state
  const [needs2FA, setNeeds2FA] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [validate2FA, { isLoading: validating2FA }] = useValidate2FAMutation();

  // Redirect authenticated users away from login page (App route also uses AuthenticatedHomeRedirect)
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const loadPublicSSOProviders = async () => {
      const tenantSlug = getTenantSlug();

      // On main domain (no tenant slug), skip tenant-scoped SSO provider lookup.
      if (!tenantSlug) {
        setAvailableSSOProviders([]);
        setLoadingSSOProviders(false);
        return;
      }

      try {
        setLoadingSSOProviders(true);
        const response = await fetch(`${apiUrl}/sso/providers/public/${tenantSlug}`);
        if (!response.ok) {
          setAvailableSSOProviders([]);
          return;
        }

        const payload = await response.json();
        const providerTypes = Array.isArray(payload?.data)
          ? payload.data.map((provider: any) => provider.type)
          : [];

        setAvailableSSOProviders(providerTypes);
      } catch {
        setAvailableSSOProviders([]);
      } finally {
        setLoadingSSOProviders(false);
      }
    };

    loadPublicSSOProviders();
  }, [apiUrl]);

  const isGoogleAvailable = availableSSOProviders.includes('GOOGLE');
  const isMicrosoftAvailable = availableSSOProviders.includes('MICROSOFT');

  const [discoverTenant] = useDiscoverTenantForLoginMutation();
  const [discoveredOrg, setDiscoveredOrg] = useState<{
    slug: string;
    name: string;
    logoUrl?: string;
    ssoTypes: string[];
  } | null>(null);

  const finishLogin = (
    authUser: User,
    tokens: { accessToken: string; refreshToken: string },
  ) => {
    setLoading(false);
    setSignInSuccess(true);
    toast.success('Login successful!');
    window.setTimeout(() => {
      if (authUser.id) {
        storage.clearFollowUpReminderDismissed(authUser.id);
      }
      dispatch(setTokens({
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
      }));
      dispatch(updateUser(authUser));
      localStorage.setItem('browserTimezone', getBrowserTimeZone());

      if (isWorkspaceRouterEnabled()) {
        void navigateAfterLogin(navigate, authUser as User, entryProductId, tokens.accessToken);
      } else {
        void resolveAuthenticatedHomeAfterAuth({
          settings: authUser.tenant?.settings,
          role: authUser.role,
          entryProductId,
          accessToken: tokens.accessToken,
        }).then((route) => navigate(route, { replace: true }));
      }
    }, LOGIN_SUCCESS_NAV_DELAY_MS);
  };

  const handleEmailBlur = async () => {
    const email = formData.email.trim();
    if (!email.includes('@') || getTenantSlug()) return;
    try {
      const result = await discoverTenant({ email }).unwrap();
      const tenants = result?.tenants ?? [];
      if (tenants.length === 1) {
        setDiscoveredOrg(tenants[0]);
      } else {
        setDiscoveredOrg(null);
      }
    } catch {
      setDiscoveredOrg(null);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email) {
      errors.email = t('auth.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = t('auth.emailInvalid');
    }

    if (!formData.password) {
      errors.password = t('auth.passwordRequired');
    } else if (formData.password.length < 6) {
      errors.password = t('auth.passwordMinLength');
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setSignInSuccess(false);
    setError(null);

    const MAX_RETRIES = 2;
    const RETRY_BASE_MS = 800;

    const attemptLogin = async (attempt: number): Promise<Response> => {
      const res = await fetch(`${apiUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, password: formData.password }),
      });
      if (res.status >= 500 && attempt < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_BASE_MS * Math.pow(2, attempt)));
        return attemptLogin(attempt + 1);
      }
      return res;
    };

    try {
      const res = await attemptLogin(0);

      let json: any = null;
      try {
        json = await res.json();
      } catch {
        if (res.status === 429) {
          throw new Error('Too many login attempts. Please wait a few minutes and try again.');
        }
        if (res.status >= 500) {
          throw new Error('The server is temporarily unavailable. Please try again shortly.');
        }
        throw new Error('Unable to reach the server. Please check your connection.');
      }

      if (!res.ok) {
        const serverText =
          (typeof json?.error === 'string' && json.error) ||
          (typeof json?.message === 'string' && json.message) ||
          '';
        if (res.status === 429) {
          throw new Error('Too many login attempts. Please wait a few minutes and try again.');
        }
        if (res.status === 401 || res.status === 403) {
          throw new Error(serverText || 'Invalid email or password.');
        }
        if (res.status === 423) {
          throw new Error(serverText || 'Account temporarily locked. Try again later.');
        }
        if (res.status === 422) {
          const first =
            json?.errors && typeof json.errors === 'object'
              ? Object.values(json.errors as Record<string, string[]>).flat()[0]
              : '';
          throw new Error(first || serverText || 'Please check your email and password.');
        }
        if (res.status >= 500) {
          throw new Error('The server is temporarily unavailable. Please try again shortly.');
        }
        throw new Error(serverText || 'Login failed. Please try again.');
      }

      const authData = json?.data;

      if (authData?.requires2FA && authData?.tempToken) {
        setNeeds2FA(true);
        setTempToken(authData.tempToken);
        setError(null);
        toast.info('Enter your 2FA code to continue');
        setLoading(false);
        return;
      }

      if (!authData?.user || !authData?.tokens) {
        throw new Error('Invalid server response');
      }

      finishLogin(authData.user as User, authData.tokens);
    } catch (err: any) {
      setError(err?.message || 'Login failed');
      toast.error(err?.message || 'Login failed');
      setSignInSuccess(false);
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handle2FASubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Enter a valid 6-digit code');
      return;
    }
    try {
      const result: any = await validate2FA({ tempToken, code: totpCode }).unwrap();
      if (result?.user && result?.tokens) {
        storage.setUser(result.user);
        storage.setAccessToken(result.tokens.accessToken);
        storage.setRefreshToken(result.tokens.refreshToken);
        finishLogin(result.user as User, result.tokens);
        return;
      }
      toast.error('Invalid server response');
    } catch (err: any) {
      const message =
        err?.data?.error ||
        err?.data?.message ||
        err?.message ||
        'Invalid 2FA code';
      toast.error(typeof message === 'string' ? message : 'Invalid 2FA code');
    }
  };

  if (needs2FA) {
    return (
      <AuthSingleCardLayout
        title="Two-Factor Authentication"
        subtitle="Enter the 6-digit code from your authenticator app"
        entryProductId={entryProductId}
      >
        <form className="space-y-6" onSubmit={handle2FASubmit}>
          <Input
            id="totp"
            name="totp"
            type="text"
            label="Authentication Code"
            placeholder="000000"
            value={totpCode}
            onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            leftIcon={<FiShield className="w-5 h-5" />}
            fullWidth
            required
            autoComplete="one-time-code"
          />

          <Button type="submit" variant="primary" size="lg" fullWidth isLoading={validating2FA} className="auth-submit-btn">
            Verify &amp; Sign in
          </Button>

          <button
            type="button"
            onClick={() => { setNeeds2FA(false); setTempToken(''); setTotpCode(''); }}
            className="w-full text-center text-sm font-medium text-slate-500 hover:text-slate-900"
          >
            ← Back to login
          </button>
        </form>
      </AuthSingleCardLayout>
    );
  }

  return (
    <AuthSingleCardLayout
      title={entryProductId !== 'pm' ? t('auth.signInToProduct', { product: PRODUCT_BY_ID[entryProductId].name }) : t('auth.welcomeBack')}
      subtitle={entryProductId !== 'pm' ? t('auth.suiteSubtitle') : t('auth.signInSubtitle')}
      entryProductId={entryProductId}
    >
      <form className="space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div className="flex items-start rounded-2xl border border-red-200 bg-red-50 p-4">
            <FiAlertCircle className="mr-3 mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-700">Authentication Error</h3>
              <p className="mt-1 text-sm text-red-700/80">{error}</p>
            </div>
          </div>
        )}

        <Input
          id="email"
          name="email"
          type="email"
          label={t('auth.email')}
          placeholder="you@example.com"
          value={formData.email}
          onChange={handleChange}
          onBlur={() => void handleEmailBlur()}
          error={validationErrors.email}
          leftIcon={<FiMail className="w-5 h-5" />}
          fullWidth
          required
          autoComplete="email"
        />

        {discoveredOrg && (
          <div className="flex items-center gap-3 rounded-xl border border-indigo-100 bg-indigo-50/50 px-3 py-2">
            {discoveredOrg.logoUrl ? (
              <TenantLogoImage src={discoveredOrg.logoUrl} alt="" className="h-8 w-8 rounded-lg object-contain" />
            ) : (
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-xs font-bold text-indigo-700 border border-indigo-100">
                {discoveredOrg.name.slice(0, 1)}
              </span>
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800 truncate">{discoveredOrg.name}</p>
              <p className="text-[10px] text-slate-500">Signing in to your organization</p>
            </div>
          </div>
        )}

        <Input
          id="password"
          name="password"
          type="password"
          label="Password"
          placeholder="••••••••"
          value={formData.password}
          onChange={handleChange}
          error={validationErrors.password}
          leftIcon={<FiLock className="w-5 h-5" />}
          fullWidth
          required
          autoComplete="current-password"
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand transition-colors focus:ring-brand"
            />
            <label
              htmlFor="remember-me"
              className="ml-2 block cursor-pointer select-none text-sm text-slate-700"
            >
              Remember me
            </label>
          </div>

          <div className="text-sm">
            <Link
              to="/forgot-password"
              className="font-semibold text-brand hover:text-brand-dark transition-colors"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          isLoading={loading}
          disabled={signInSuccess}
          className={`login-signin-btn${signInSuccess ? ' login-signin-btn--success' : ''}`}
        >
          <span className="login-signin-btn__label">{t('auth.signIn')}</span>
        </Button>

        <div className="text-center">
          <p className="text-sm text-slate-600">
            Don&apos;t have an account?{' '}
            <Link
              to="/register"
              className="font-semibold text-brand hover:text-brand-dark transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>

        {!loadingSSOProviders && (isGoogleAvailable || isMicrosoftAvailable) && (
          <div className="border-t border-slate-200 pt-6">
            <p className="mb-4 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
              Or continue with SSO
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {isGoogleAvailable && (
                <a
                  href={`${apiUrl}/auth/sso/google`}
                  className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                >
                  <SiGoogle className="h-4 w-4" />
                  Google
                </a>
              )}
              {isMicrosoftAvailable && (
                <a
                  href={`${apiUrl}/auth/sso/microsoft`}
                  className="flex items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-white"
                >
                  <FiShield className="h-4 w-4" />
                  Microsoft
                </a>
              )}
            </div>
          </div>
        )}
      </form>
    </AuthSingleCardLayout>
  );
}
